import { useEffect, useMemo, useRef, useState } from 'react'
import { fileInputType } from './NewOrder'
import axios from 'axios'
import './OrderDocuments.css'
import { tempFiles } from './Correction'
import {
  Alert,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Radio,
  TextField,
  Typography,
} from '@mui/material'
import { DeleteOutline, UploadFile } from '@mui/icons-material'
import { calculateGp } from '../helper'
import { confidenceType, orderType } from '../types'
import { useClassList } from '../hooks'

function OrderDocuments({
  order,
  files,
  originalFiles,
  orderConf,
  isCorrection,
  confChanges,
  setView,
  setFiles,
  setOrder,
}: {
  order: orderType
  files: fileInputType
  orderConf: React.MutableRefObject<confidenceType>
  isCorrection: boolean
  confChanges: React.MutableRefObject<confidenceType>
  setOrder: React.Dispatch<React.SetStateAction<orderType>>
  setFiles: React.Dispatch<React.SetStateAction<fileInputType>>
  setView: React.Dispatch<React.SetStateAction<number>>
  originalFiles: tempFiles | null
}) {
  const classList = useClassList()

  const [orderCategory, setOrderCategory] = useState('')
  const [riskBuyCompliant, setRiskBuyCompliant] = useState(false)
  const [aiState, setAiState] = useState<'loading' | 'error' | ''>('')
  const [aiWarnings, setAiWarnings] = useState({
    differentOrderTotals: false,
    vendorExpired: false,
    cqExpired: false,
    negativeMargin: false,
  })

  const impresAiResult = useRef<
    Partial<{
      QuoteNumber: string
      PurchaseVehicle: string
      OrderTotal: number
      QuoteDate: Date
      QuoteExpires: Date
    }>
  >({})
  const govAiResult = useRef<
    Partial<{
      CustomerPO: string
      GovPaymentTerms: string
      DeliveryDueDate: Date
      AwardDate: Date
      OrderTotal: number
    }>
  >({})
  const vendorAiResult = useRef<
    Partial<{
      VendorPaymentTerms: string
      QuoteExpires: Date
      ShippingCharges: number
      TotalCost: number
    }>[]
  >([])
  const bypassAiWarnings = useRef(false)

  const DocumentUpload = useMemo(
    () =>
      ({
        label,
        required = false,
        multiple = false,
      }: {
        label: 'IMPRES Quote' | 'Government Award' | 'Vendor Quote' | 'Miscellaneous'
        required?: boolean
        multiple?: boolean
      }) => {
        const key = label.replaceAll(' ', '').toLowerCase() as keyof fileInputType

        return (
          <div className="order-documents-item">
            <Typography>
              {label} {required ? '(Required)' : ''}
            </Typography>
            <div className="button-container">
              <Button
                variant="outlined"
                component="label"
                size="large"
                startIcon={<UploadFile />}
                onDragOver={handleDragOver}
                onDrop={handleDrop(key, multiple)}
              >
                Choose File
                <input
                  type="file"
                  multiple={multiple}
                  onChange={e => {
                    setFiles(prev => ({
                      ...prev,
                      [key]: [...(multiple ? prev[key] : []), ...Array.from(e.target.files ?? [])],
                    }))
                  }}
                />
              </Button>
              <Button
                disabled={files[key].length === 0}
                onClick={() => {
                  setFiles(prev => ({ ...prev, [key]: [] }))
                }}
              >
                Clear
              </Button>
            </div>
            <List dense className="order-documents-list">
              {files[key].map(file => (
                <ListItem disablePadding key={file.name}>
                  <ListItemText
                    primary={
                      <div className="order-documents-list-item-text">
                        {file.name}
                        <IconButton
                          onClick={() =>
                            setFiles(prev => ({ ...prev, [key]: prev[key].filter(f => f.name !== file.name) }))
                          }
                        >
                          <DeleteOutline />
                        </IconButton>
                      </div>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </div>
        )
      },
    [files, setFiles],
  )

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
  }

  const handleDrop = (key: keyof fileInputType, multiple: boolean) => (e: React.DragEvent) => {
    e.preventDefault()
    const dropped = Array.from(e.dataTransfer.files)
    setFiles(prev => ({ ...prev, [key]: multiple ? [...prev[key], ...dropped] : dropped.slice(0, 1) }) as fileInputType)
  }

  const handleReadAi = () => {
    if (isCorrection || bypassAiWarnings.current) {
      setView(1)
    } else {
      setAiState('loading')
      setAiWarnings({
        differentOrderTotals: false,
        vendorExpired: false,
        cqExpired: false,
        negativeMargin: false,
      })

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const aiPromises: Promise<any>[] = []
      for (const i of [
        { files: files.impresquote, label: 'impresQuote' },
        { files: files.governmentaward, label: 'govAward' },
        { files: files.vendorquote, label: 'vendorQuote' },
      ]) {
        for (const file of Array.from(i.files)) {
          if (file.name.endsWith('.pdf')) {
            const AIfile = new FormData()
            AIfile.append('file', file, i.label + '_' + file.name)
            aiPromises.push(
              axios
                .post('/AI', AIfile)
                .then(res => {
                  if (res.data !== 'Not a PDF') {
                    switch (i.label) {
                      case 'impresQuote':
                        impresAiResult.current = res.data.order
                        orderConf.current = { ...orderConf.current, ...res.data.confidence }
                        break
                      case 'govAward':
                        govAiResult.current = res.data.order
                        orderConf.current = { ...orderConf.current, ...res.data.confidence }
                        break
                      case 'vendorQuote':
                        vendorAiResult.current.push(res.data.order)
                        orderConf.current = {
                          ...orderConf.current,
                          VendorPaymentTerms: res.data.confidence.VendorPaymentTerms,
                          ShippingCharges: res.data.confidence.ShippingCharges,
                          TotalCost: res.data.confidence.TotalCost,
                        }
                        break
                    }
                  }
                })
                .catch(err => {
                  if (err.response) {
                    // received error code other than 2xx
                    console.error(err.response)
                  } else if (err.request) {
                    // no response received
                    console.error(err.request)
                  } else {
                    // request was not made
                    console.error(err.message)
                  }
                  throw err
                }),
            )
          }
        }
      }
      Promise.all(aiPromises)
        .then(() => {
          if (
            govAiResult.current.OrderTotal &&
            impresAiResult.current.OrderTotal &&
            govAiResult.current.OrderTotal !== impresAiResult.current.OrderTotal
          ) {
            setAiWarnings(prev => ({ ...prev, differentOrderTotals: true }))
          }
          if (vendorAiResult.current.some(vendor => vendor.QuoteExpires && vendor.QuoteExpires < new Date())) {
            setAiWarnings(prev => ({ ...prev, vendorExpired: true }))
          }
          if (impresAiResult.current.QuoteExpires && impresAiResult.current.QuoteExpires < new Date()) {
            setAiWarnings(prev => ({ ...prev, cqExpired: true }))
          }
          if (
            govAiResult.current.OrderTotal &&
            vendorAiResult.current.map(vendor => vendor.TotalCost ?? 0).reduce((sum, val) => sum + val, 0) <
              govAiResult.current.OrderTotal
          ) {
            setAiWarnings(prev => ({ ...prev, negativeMargin: true }))
          }

          if (Object.values(aiWarnings).every(value => !value)) {
            setOrder(prev => ({
              ...prev,
              ...impresAiResult.current,
              ...govAiResult.current,
              ...vendorAiResult.current.reduce(
                (acc, vendorQuote) => ({
                  VendorPaymentTerms: vendorQuote.VendorPaymentTerms ?? acc.VendorPaymentTerms,
                  QuoteExpires: vendorQuote.QuoteExpires ?? acc.QuoteExpires,
                  ShippingCharges: (vendorQuote.ShippingCharges ?? 0) + acc.ShippingCharges,
                  TotalCost: (vendorQuote.TotalCost ?? 0) + acc.TotalCost,
                }),
                {
                  VendorPaymentTerms: null as string | null,
                  QuoteExpires: null as Date | null,
                  ShippingCharges: 0,
                  TotalCost: 0,
                },
              ),
            }))

            for (const vendor of vendorAiResult.current) {
              setOrder(prev => ({ ...prev, VendorPaymentTerms: vendor.VendorPaymentTerms ?? null }))
              if (!Number.isNaN(Number(vendor.TotalCost))) {
                setOrder(prev => ({ ...prev, TotalCost: (prev.TotalCost ?? 0) + (vendor.TotalCost ?? 0) }))
              }
              if (!Number.isNaN(Number(vendor.ShippingCharges))) {
                setOrder(prev => ({
                  ...prev,
                  ShippingCharges: (prev.ShippingCharges ?? 0) + (vendor.ShippingCharges ?? 0),
                }))
              }
            }

            setOrder(prev => ({
              ...prev,
              GrossMargin:
                order.PurchaseVehicle === 'ICPT UCOR'
                  ? 0
                  : calculateGp(
                      prev.OrderTotal ?? 0,
                      prev.TotalCost ?? 0,
                      prev.PurchaseVehicle ?? '',
                      prev.IsCreditCard || prev.GovPaymentTerms === 'Credit Card',
                    ),
            }))

            confChanges.current = { ...confChanges.current, ...orderConf.current }
            setAiState('')
            setView(1)
          }
        })
        .catch(() => {
          setAiState('error')
        })
    }
  }

  useEffect(() => {
    if (order.PurchaseVehicle === 'GSA') {
      setOrderCategory('GSA')
    } else if (order.PurchaseVehicle?.includes('SEWP')) {
      setOrderCategory('SEWP')
    } else if (!!order.PurchaseVehicle) {
      setOrderCategory('Regular')
    }
  }, [order.PurchaseVehicle, classList])

  useEffect(() => {
    if (originalFiles) {
      setFiles({
        impresquote: Array.from(originalFiles.IMPRESQuote.files),
        governmentaward: Array.from(originalFiles.GovernmentAward.files),
        vendorquote: Array.from(originalFiles.VendorQuote.files),
        miscellaneous: Array.from(originalFiles.RelatedDoc.files),
      })
    }
  }, [])

  useEffect(() => {
    setOrder(prev => ({ ...prev, MultipleVendorQuotes: files.vendorquote.length > 1 }))
  }, [files])

  return (
    <>
      <div className="order-documents-container">
        <Typography variant="h6">Order Type</Typography>
        <div className="order-documents-content">
          <Grid container spacing="24px">
            <Grid size={4}>
              <div className="order-documents-item">
                <FormControlLabel
                  control={
                    <Radio
                      value="Regular"
                      checked={orderCategory === 'Regular'}
                      onChange={e => {
                        setOrderCategory(e.target.value)
                      }}
                    />
                  }
                  label="Regular"
                />
                <TextField
                  select
                  fullWidth
                  label="Contract Type"
                  size="small"
                  disabled={orderCategory !== 'Regular'}
                  value={
                    orderCategory === 'Regular' &&
                    !order.PurchaseVehicle?.includes('SEWP') &&
                    order.PurchaseVehicle !== 'GSA'
                      ? order.PurchaseVehicle
                      : ''
                  }
                  onChange={e =>
                    setOrder(prev => ({
                      ...prev,
                      PurchaseVehicle: e.target.value,
                      ContractNumber:
                        classList
                          .find(cls => cls.name === e.target.value)
                          ?.children?.find(cls => cls.availableonheader === 'T')?.name ?? e.target.value,
                    }))
                  }
                >
                  {classList
                    .filter(cls => !cls.name.includes('SEWP') && cls.name !== 'GSA')
                    .map(cls => (
                      <MenuItem key={cls.id} value={cls.name}>
                        {cls.name}
                      </MenuItem>
                    ))}
                </TextField>
              </div>
            </Grid>
            <Grid size={4}>
              <div className="order-documents-item">
                <FormControlLabel
                  control={
                    <Radio
                      value="GSA"
                      checked={orderCategory === 'GSA'}
                      onChange={e => {
                        setOrderCategory(e.target.value)
                      }}
                    />
                  }
                  label="GSA"
                />
                <FormControlLabel
                  control={<Checkbox />}
                  disabled={orderCategory !== 'GSA'}
                  label="This order has been confirmed to be compliant and/or approved"
                  checked={order.PurchaseVehicle === 'GSA' && orderCategory === 'GSA'}
                  onChange={(_e, checked) =>
                    setOrder(prev => ({
                      ...prev,
                      PurchaseVehicle: checked ? 'GSA' : '',
                      ContractNumber: checked
                        ? classList
                            .find(cls => cls.name === 'GSA')
                            ?.children?.find(cls => cls.availableonheader === 'T')?.name ?? 'GSA'
                        : '',
                    }))
                  }
                />
              </div>
            </Grid>
            <Grid size={4}>
              <div className="order-documents-item">
                <FormControlLabel
                  control={
                    <Radio
                      value="SEWP"
                      checked={orderCategory === 'SEWP'}
                      onChange={e => {
                        setOrderCategory(e.target.value)
                      }}
                    />
                  }
                  label="SEWP"
                />
                <TextField
                  select
                  fullWidth
                  label="Contract Type"
                  size="small"
                  disabled={orderCategory !== 'SEWP'}
                  value={
                    orderCategory === 'SEWP' && order.PurchaseVehicle?.includes('SEWP') ? order.PurchaseVehicle : ''
                  }
                  onChange={e =>
                    setOrder(prev => ({
                      ...prev,
                      PurchaseVehicle: e.target.value,
                      ContractNumber:
                        classList
                          .find(cls => cls.name === e.target.value)
                          ?.children?.find(cls => cls.availableonheader === 'T')?.name ?? e.target.value,
                    }))
                  }
                >
                  {classList
                    .filter(cls => cls.name.includes('SEWP'))
                    .map(cls => (
                      <MenuItem key={cls.id} value={cls.name}>
                        {cls.name}
                      </MenuItem>
                    ))}
                </TextField>
              </div>
            </Grid>
          </Grid>
          <Divider flexItem />
          <div className="order-documents-risk-buy">
            <FormControlLabel
              control={<Checkbox checked={!!order.RiskBuy} />}
              label="Risk Buy"
              onChange={(_e, checked) => {
                setOrder(prev => ({ ...prev, RiskBuy: checked }))
                !checked && setRiskBuyCompliant(false)
              }}
            />
            <FormControlLabel
              control={<Checkbox checked={riskBuyCompliant} />}
              label="This order has been confirmed to be compliant and/or approved"
              disabled={!order.RiskBuy}
              onChange={(_e, checked) => setRiskBuyCompliant(checked)}
            />
          </div>
          <div>
            <FormControlLabel
              control={<Checkbox checked={!!order.Urgent} />}
              label="This order is URGENT"
              onChange={(_e, checked) => {
                setOrder(prev => ({ ...prev, Urgent: checked }))
                if (!checked) {
                  setOrder(prev => ({ ...prev, UrgentReason: null }))
                }
              }}
            />
            <TextField
              select
              fullWidth
              label="Reason"
              size="small"
              disabled={!order.Urgent}
              value={order.UrgentReason ?? ''}
              onChange={e => {
                setOrder(prev => ({ ...prev, UrgentReason: e.target.value }))
              }}
            >
              <MenuItem value="PoP">PoP</MenuItem>
              <MenuItem value="Cost Implications">Cost Implications</MenuItem>
            </TextField>
          </div>
          <FormControlLabel
            control={<Checkbox checked={!!order.IsCreditCard} />}
            label={
              <>
                This order will be paid for by credit card
                <br />
                The card number, expiration date, security code, address, and name are already in PayTrace
                <br />
                This order is reported as a credit card order in Contraqer
              </>
            }
            onChange={(_e, checked) => {
              setOrder(prev => ({
                ...prev,
                IsCreditCard: checked,
                GovPaymentTerms: checked
                  ? 'Credit Card'
                  : prev.GovPaymentTerms !== 'Credit Card'
                  ? prev.GovPaymentTerms
                  : 'Net 30',
              }))
            }}
          />
        </div>
      </div>
      <div className="order-documents-container">
        <Typography variant="h6">Document Upload</Typography>
        <div className="order-documents-content">
          <Grid container spacing="24px">
            <Grid size={3}>
              <DocumentUpload label="IMPRES Quote" required={order.PurchaseVehicle !== 'ICPT UCOR'} />
            </Grid>
            <Grid size={3}>
              <DocumentUpload label="Government Award" required />
            </Grid>
            <Grid size={3}>
              <DocumentUpload label="Vendor Quote" required={order.PurchaseVehicle !== 'ICPT UCOR'} multiple />
            </Grid>
            <Grid size={3}>
              <DocumentUpload label="Miscellaneous" multiple />
            </Grid>
          </Grid>
        </div>
      </div>
      <div className="order-documents-next-button">
        {aiState === 'error' && (
          <Alert severity="error" variant="filled" onClose={() => setAiState('')}>
            There was an error connecting to the AI model. Please try again.
          </Alert>
        )}
        <Button
          variant="contained"
          onClick={handleReadAi}
          disabled={
            (!order.PurchaseVehicle && !orderCategory) ||
            (order.PurchaseVehicle !== 'ICPT UCOR' && files.impresquote.length === 0) ||
            files.governmentaward.length === 0 ||
            (order.PurchaseVehicle !== 'ICPT UCOR' && files.vendorquote.length === 0) ||
            (order.Urgent && !order.UrgentReason) ||
            (orderCategory === 'Regular' &&
              (order.PurchaseVehicle?.includes('SEWP') || order.PurchaseVehicle === 'GSA' || !order.PurchaseVehicle)) ||
            (orderCategory === 'GSA' && order.PurchaseVehicle !== 'GSA') ||
            (orderCategory === 'SEWP' && !order.PurchaseVehicle?.includes('SEWP')) ||
            (order.RiskBuy && !riskBuyCompliant)
          }
          loading={aiState === 'loading'}
        >
          Next
        </Button>
      </div>
    </>
  )
}

export default OrderDocuments
